// frontend/src/components/boarding/NoShowModal.js
import React, { useState } from 'react';
import { X, UserX, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import boardingService from '../../services/boardingService';

/**
 * NoShowModal
 *
 * Allows driver to mark passengers as no-show
 * Captures reason and notes for accountability
 * Irreversible action - requires confirmation
 */
const NoShowModal = ({
  open,
  onClose,
  booking,
  tripId,
  entityId,
  onSuccess
}) => {
  const { t } = useTranslation();
  const { background, text, cn } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState('passengerNotArrived');
  const [customNotes, setCustomNotes] = useState('');

  /**
   * Handle no-show submission
   */
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // Build notes based on reason and custom notes
      let notes = '';
      if (reason === 'other') {
        notes = customNotes;
      } else {
        notes = t(reason);
        if (customNotes) {
          notes += ` - ${customNotes}`;
        }
      }

      // Submit no-show
      const response = await boardingService.markAsNoShow(
        entityId,
        tripId,
        booking.bookingId,
        notes
      );

      if (response.success) {
        // Success
        if (onSuccess) {
          onSuccess(response.data);
        }
        onClose();
      } else {
        setError(response.error || 'Failed to mark as no-show');
      }
    } catch (err) {
      console.error('No-show error:', err);

      const errorCode = err.response?.data?.code;
      let errorMessage = 'Failed to mark as no-show';

      if (errorCode === 'ALREADY_BOARDED') {
        errorMessage = t('alreadyBoarded') + ' - Cannot mark as no-show';
      } else if (errorCode === 'BOOKING_NOT_FOUND') {
        errorMessage = 'Booking not found';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!open || !booking) return null;

  const isCustomReasonRequired = reason === 'other' && !customNotes.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className={cn("max-w-md w-full rounded-lg shadow-xl", background.secondary)}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <UserX className="h-5 w-5 text-red-500" />
            <h2 className={cn("text-xl font-semibold", text.primary)}>{t('markAsNoShow')}</h2>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
            </div>
          )}

          {/* Warning Alert */}
          <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  {t('confirmNoShow')}
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                  {t('noShowIrreversible')}
                </p>
              </div>
            </div>
          </div>

          {/* Passenger Information */}
          <div className="mb-6">
            <p className={cn("text-sm font-medium mb-2", text.secondary)}>
              {t('riders')}
            </p>
            <h3 className={cn("text-lg font-semibold mb-1", text.primary)}>
              {booking.passengerName}
            </h3>
            <p className={cn("text-sm", text.secondary)}>
              {booking.bookingRiders} {t('riders')} • {booking.pickup} → {booking.dropoff}
            </p>
          </div>

          <div className="h-px bg-gray-200 dark:border-gray-700 my-4"></div>

          {/* Reason Selection */}
          <div className="mb-6">
            <label className={cn("block text-sm font-medium mb-2", text.primary)}>
              {t('noShowReason')}
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={cn(
                "w-full px-3 py-2 rounded border",
                "border-gray-300 dark:border-gray-600",
                "bg-white dark:bg-gray-800",
                text.primary,
                "focus:outline-none focus:ring-2 focus:ring-blue-500"
              )}
            >
              <option value="passengerNotArrived">{t('passengerNotArrived')}</option>
              <option value="passengerMissedBus">{t('passengerMissedBus')}</option>
              <option value="other">{t('other')}</option>
            </select>
          </div>

          {/* Additional Notes */}
          <div className="mb-4">
            <label className={cn("block text-sm font-medium mb-2", text.primary)}>
              {t('notes')} (${t('optional')})
            </label>
            <textarea
              rows="3"
              value={customNotes}
              onChange={(e) => setCustomNotes(e.target.value)}
              placeholder={t('enterAnyNotes')}
              className={cn(
                "w-full px-3 py-2 rounded border",
                isCustomReasonRequired
                  ? "border-red-300 dark:border-red-600"
                  : "border-gray-300 dark:border-gray-600",
                "bg-white dark:bg-gray-800",
                text.primary,
                "focus:outline-none focus:ring-2",
                isCustomReasonRequired
                  ? "focus:ring-red-500"
                  : "focus:ring-blue-500",
                "resize-none"
              )}
            />

          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || isCustomReasonRequired}
            className="flex items-center gap-2 px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserX className="h-4 w-4" />
            {loading ? t('loading') : t('confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoShowModal;
